import React, { useState } from "react";
import { useHistory } from "react-router-dom";

import { Backdrop, CircularProgress, makeStyles } from "@material-ui/core";

import {
  Edit,
  Loading,
  required,
  SimpleForm,
  Toolbar,
  TopToolbar,
  useEditController,
} from "react-admin";

import { useUser } from "../../../authenticatorApp/UserContext";
import { BackButton } from "../../../components/common/BackButton";
import { ForceTitle } from "../../../components/common/ForceTitle";
import { FormColumns } from "../../../components/common/FormColumns";
import { FormSection } from "../../../components/common/FormSection";
import { NotesTabShow } from "../../../components/common/NotesTab";
import {
  ScheduleDateTimeInput,
  useScheduleEventType,
} from "../../../components/common/scheduler/ScheduleDateTimeInput";
import { Workflow } from "../../../utils/constants";
import { getBackPath } from "../../../utils/utils";

import { useInboundScheduleGuard } from "../dispatchGuard";
import { DispatchSaveButton } from "./DispatchSaveButton";
import { InboundAside } from "./InboundAside";
import { getInboundExceptions, ShipmentSetupTab } from "./InboundShow";

const useStyles = makeStyles((theme) => ({
  backdrop: {
    zIndex: theme.zIndex.drawer + 1,
    color: "#fff",
  },
}));

const validateRequired = required();

// Shared with InboundDispatchScheduleEdit — both carrier delivery forms save the
// schedule the same way and offer the same single action.
export const DispatchEditActions = ({ backPath, ...props }) => (
  <TopToolbar>
    <BackButton {...props} backPath={backPath} />
  </TopToolbar>
);

export const DispatchEditToolbar = ({
  record,
  backPath,
  selectedTimeslot,
  ...props
}) => {
  const classes = useStyles();
  const [isSaving, setIsSaving] = useState(false);

  return (
    <Toolbar {...props}>
      <Backdrop className={classes.backdrop} open={isSaving}>
        <CircularProgress color="inherit" />
      </Backdrop>
      <DispatchSaveButton
        {...props}
        record={record}
        backPath={backPath}
        selectedTimeslot={selectedTimeslot}
        isSaving={isSaving}
        setIsSaving={setIsSaving}
      />
    </Toolbar>
  );
};

/**
 * Rescheduling a delivery the carrier already holds: the same two columns as the
 * claim form — the delivery on the left to check against, the calendar on the
 * right — with the notes underneath, the one section that reads better full
 * width. The dispatch times a shipment collects on its way through the yard are
 * left out: nothing has happened to it yet while it is still only scheduled,
 * so the section had nothing to show.
 */
export const InboundDispatchEdit = (props) => {
  const { record } = useEditController(props);
  const { appUser } = useUser();
  const history = useHistory();
  const backPath = getBackPath(props);
  const { eventType, canSchedule, selectedTimeslot, setSelectedTimeslot } =
    useScheduleEventType(
      record,
      Workflow.INBOUND,
      record?.account?.inboundEventTypeId,
    );
  const blocked = useInboundScheduleGuard(record, props.basePath);

  if (blocked || !record?.account) return <Loading />;
  return (
    <Edit
      {...props}
      title={<ForceTitle title="Schedule Delivery" />}
      mutationMode="pessimistic"
      actions={<DispatchEditActions {...props} backPath={backPath} />}
      aside={
        <InboundAside
          {...props}
          history={history}
          backPath={backPath}
          exceptions={getInboundExceptions(record)}
        />
      }
    >
      {/* Tally sheet is yard-side detail; carriers never see it, here or on
          the show page. */}
      <SimpleForm
        variant="outlined"
        submitOnEnter={false}
        toolbar={
          <DispatchEditToolbar
            {...props}
            record={record}
            backPath={backPath}
            selectedTimeslot={selectedTimeslot}
          />
        }
      >
        <FormColumns>
          <FormSection title="Inbound Info">
            <ShipmentSetupTab {...props} />
          </FormSection>
          <FormSection title="Schedule Delivery">
            <ScheduleDateTimeInput
              inline
              id="reschedule-input"
              label="Delivery Appointment"
              account={record.account}
              workflow={Workflow.INBOUND}
              eventType={eventType}
              canSchedule={canSchedule}
              selectedTimeslot={selectedTimeslot}
              setSelectedTimeslot={setSelectedTimeslot}
              validate={validateRequired}
              fullWidth={true}
              helperText={
                canSchedule
                  ? false
                  : `Contact ${appUser?.reloadYard?.name} to setup an event type.`
              }
            />
          </FormSection>
        </FormColumns>
        <FormSection title="Yard Notes">
          <NotesTabShow {...props} hidePrivate={true} hideLabel={true} />
        </FormSection>
      </SimpleForm>
    </Edit>
  );
};
